import { plural } from './plural'

/** Strings for the agent editor, its prompt page and the agent form page. */
export const agentEditorCopy = {
  retry: 'Retry',
  loadAgentFailed: 'Could not load this agent.',
  loadTemplatesFailed: 'Could not load templates.',
  loadTopicsFailed: 'Could not load topics.',
  loadRunsFailed: 'Could not load runs.',
  description: 'Description',
  blankTemplate: 'Blank',
  promptStats: (chars: number, tokens: number) =>
    `${chars.toLocaleString()} characters · about ${tokens.toLocaleString()} tokens`,
  unsavedChanges: 'Unsaved changes',

  form: {
    requestIdUnavailable:
      'This browser cannot create a request ID — use a current browser.',
    newAgentTitle: 'New agent',
    editAgentTitle: 'Edit agent',
    newAgentDescription: 'Start from scratch or a template.',
    workspaceCrumb: 'Workspace',
    agentsCrumb: 'Agents',
    newCrumb: 'New',
    editCrumb: 'Edit',
    promptCrumb: 'Prompt',
    saveTemplateHeading: 'Save as template',
    nameLabel: 'Name',
    optionalHint: '(optional)',
    cancel: 'Cancel',
    saveTemplate: 'Save template',
    templateSaved: (name: string) => `Template "${name}" saved`,
    templateLabel: 'Start from a template',
    builtIn: 'Built-in',
    topicLoadFailed: 'Could not load the topic.',
    notFound:
      'Agent not found — check the link or open the agent from its workspace.',
    lifecycleFailed: "Failed to change the agent's status — try again.",
  },

  status: {
    createPendingHeading: 'Creating agent',
    createUnknownHeading: 'Could not confirm the agent was created.',
    createdHeading: 'Agent created',
    topicUnavailable: 'Agent created, but the topic is no longer available.',
    topicUnchecked:
      'Agent created, but its topic assignment could not be checked.',
    topicAddFailed: (reason?: string) =>
      reason
        ? `Agent created, but adding it to the topic failed — ${reason}`
        : 'Agent created, but adding it to the topic failed — retry the assignment.',
    assigningHeading: 'Adding the agent to the topic',
    assignmentUnknownHeading:
      'Could not confirm the agent was added to the topic.',
    retryAssignment: 'Retry assignment',
    openProfile: 'Open profile',
    returnToCaller: 'Return',
    reconcilePendingHeading: 'Runtime reconciliation pending',
  },

  editor: {
    invalidJson: 'Not valid JSON',
    invalidConfig: 'Invalid config',
    warning: (message: string) => `Warning: ${message}`,
    conforms: 'Conforms to agent-config v2',
    activate: 'Activate',
    pause: 'Pause',
    message: 'Message',
    essentials: 'Essentials',
    nameLabel: 'Name',
    modelLabel: 'Model',
    modelPlaceholder: 'Provider default',
    providerLabel: 'Provider',
    systemPrompt: 'System prompt',
    aboutTokens: (tokens: number) => `~${tokens} tokens`,
    expand: 'Expand',
    capabilities: 'Capabilities',
    advancedJson: 'Advanced · JSON',
    jsonLabel: 'agent-config v2 JSON',
    selectProvider: 'Select a provider',
    save: 'Save',
    createAgent: 'Create agent',
    saveAsTemplate: 'Save as template',
    cancel: 'Cancel',
    memberOf: 'Member of',
    primary: 'Primary',
    noTopics: 'Not in any topic yet.',
    activity: 'Activity',
    noRuns: 'No runs yet.',
    runsCount: (n: number) => plural(n, { one: '{n} run', other: '{n} runs' }),
    allRuns: 'All runs',
  },

  presets: {
    label: {
      readonly: 'Read-only',
      collab: 'Collaborator',
      builder: 'Builder',
      custom: 'Custom',
    },
    summary: {
      readonly: 'No tools.',
      collab: 'Tools, but no sub-agents or network.',
      builder: 'Tools, sub-agents and network.',
      custom: 'Set in the JSON.',
    },
  },

  diff: {
    name: 'name',
    model: 'model',
    provider: 'provider',
    description: 'description',
    routingHint: 'routing hint',
    maxIterations: 'max iterations',
    timeoutMs: 'timeout (ms)',
    maxTokens: 'max output tokens',
    maxCostUsd: 'run budget (USD)',
    sessionTtl: 'session TTL',
    reasoning: 'reasoning',
    compactionContextTokens: 'compaction · context tokens',
    compactionPreserveRecent: 'compaction · preserve recent',
    compactionToolResultCap: 'compaction · tool result cap',
    compactionSummaryStrategy: 'compaction · summary strategy',
    schedule: 'schedule',
    noCron: '(no cron)',
    removed: 'removed',
  },

  compaction: {
    usingDefaults: 'using defaults',
    overridden: (n: number) =>
      plural(n, {
        one: '{n} value overridden',
        other: '{n} values overridden',
      }),
    heading: (summary: string) => `Compaction · ${summary}`,
    maxContextTokens: 'max_context_tokens',
    maxContextTokensHelp: 'Blank uses the model default.',
    unlimited: 'Unlimited',
    preserveRecent: 'preserve_recent',
    maxToolResultTokens: 'max_tool_result_tokens',
    summaryStrategy: 'summary_strategy',
    marker: 'Marker',
    llmSummary: 'LLM summary',
    episodeSummary: 'Episode summary',
  },

  reasoning: {
    modelDefault: 'Model default',
    modelDefaultWith: (value: string) => `Model default (${value})`,
    modelDefaultNoReasoning: 'Model default (no reasoning)',
    off: 'Off',
    useModelDefault: 'Use model default',
    profileFailed: (reason: string) =>
      `Could not load this model's profile — ${reason}`,
    pickModel: 'Pick a provider and model to see its reasoning options.',
    noProfile: 'Reasoning cannot be configured for this model.',
    alwaysOn: 'This model always reasons.',
    noControl: 'This model has no reasoning control.',
    range: (min: number, max: number) => `${min} to ${max} tokens`,
    atLeast: (min: number) => `At least ${min} tokens`,
    modelDefaultToggle: (on: boolean) =>
      on ? 'Model default: on' : 'Model default: off',
    useDefault: 'Use default',
    setting: (key: string, value: string) => `${key}: ${value}`,
    heading: (summary: string) => `Reasoning · ${summary}`,
    notice:
      'This model has no extended-thinking budget — check the reasoning setting below.',
    unsupported: (summary: string) =>
      `${summary} is not a setting this model takes — choose another value or use the model default.`,
  },

  schedule: {
    heading: 'Schedule',
    entry: (n: number) => `Entry ${n}`,
    remove: 'Remove',
    cronLabel: 'Schedule (cron)',
    cronPlaceholder: '0 9 * * *',
    requiredHint: '(required)',
    taskLabel: 'Task',
    activeHours: 'active_hours',
    activeHoursPlaceholder: '09:00-17:00',
    timezone: 'Timezone',
    timezonePlaceholder: 'America/New_York',
    add: 'Add entry',
  },

  history: {
    noChanges: 'No unsaved changes',
    capabilitiesLead: 'Capabilities ·',
    promptTitle: 'system prompt',
    promptCaption: (added: number, deleted: number) =>
      `+${added} / -${deleted} lines`,
    heading: 'Prompt history',
    version: (seq: number) => `v${seq}`,
    systemAuthor: 'system',
    identical: 'Identical to current',
    diffCaption: 'diff',
    restore: 'Restore',
  },

  prompt: {
    title: 'System prompt',
    titleFor: (name: string) => `System prompt — ${name}`,
    description: 'Base instructions for this agent',
    done: 'Done',
  },
} as const
